// Radha — Settings. Currently just the receiving UPI VPA used to build payment
// links on Statements/Invoicing (and the customer portal). Restricted to
// admin/cfo/finance_controller by the DB trigger (043); the UI mirrors that so a
// non-privileged user sees why the field is read-only rather than a confusing error.
import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMe, useRole } from "../lib/authSupabase";
import { useSetTenantUpiVpa, useSetEarlyPayDiscount, useSetPoPortalThreshold, useSetDirectPayThreshold } from "../hooks/useTenantSettings";
import { useTenantSettingsAudit } from "../hooks/useTenantSettingsAudit";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { BuildStamp } from "../components/BuildStamp";
import { ErpSync } from "../components/ErpSync";
import { DigestSettings } from "../components/DigestSettings";
import { EnterpriseSettings } from "../components/EnterpriseSettings";
import { JurisdictionSettings } from "../components/JurisdictionSettings";
import { fmtMoneyFull, homeCurrency, fmtDateTime } from "../lib/money";

const CAN_SET_VPA = new Set(["admin", "cfo", "finance_controller"]);

export function SettingsPanel() {
  const me = useMe(true);
  const { canWrite } = useRole();
  const qc = useQueryClient();
  const setVpa = useSetTenantUpiVpa();
  const setDiscount = useSetEarlyPayDiscount();
  const setThreshold = useSetPoPortalThreshold();
  const setDirectPayThreshold = useSetDirectPayThreshold();
  const audit = useTenantSettingsAudit();
  const [vpa, setVpaLocal] = useState("");
  const [discPct, setDiscPct] = useState("0");
  const [discDays, setDiscDays] = useState("0");
  const [threshold, setThresholdLocal] = useState("500000");
  const [directThreshold, setDirectThresholdLocal] = useState("100000");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [discNote, setDiscNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [threshNote, setThreshNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [directThreshNote, setDirectThreshNote] = useState<{ ok: boolean; text: string } | null>(null);
  const allowed = !!me && CAN_SET_VPA.has(me.role);

  useEffect(() => { if (me?.tenant_upi_vpa != null) setVpaLocal(me.tenant_upi_vpa); }, [me?.tenant_upi_vpa]);
  useEffect(() => {
    if (me) { setDiscPct(String(me.early_pay_discount_pct)); setDiscDays(String(me.early_pay_discount_days)); }
  }, [me?.early_pay_discount_pct, me?.early_pay_discount_days]);
  useEffect(() => { if (me?.po_portal_auto_threshold != null) setThresholdLocal(String(me.po_portal_auto_threshold)); }, [me?.po_portal_auto_threshold]);
  useEffect(() => { if (me?.direct_pay_max_threshold != null) setDirectThresholdLocal(String(me.direct_pay_max_threshold)); }, [me?.direct_pay_max_threshold]);

  const save = () => {
    setNote(null);
    setVpa.mutate(vpa, {
      onSuccess: () => {
        setNote({ ok: true, text: "Saved" });
        qc.invalidateQueries({ queryKey: ["audit-events", "tenants"] });
      },
      onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  const saveDiscount = () => {
    setDiscNote(null);
    setDiscount.mutate({ pct: Number(discPct) || 0, days: Number(discDays) || 0 }, {
      onSuccess: () => {
        setDiscNote({ ok: true, text: "Saved" });
        qc.invalidateQueries({ queryKey: ["audit-events", "tenants"] });
      },
      onError: (e) => setDiscNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  const saveThreshold = () => {
    setThreshNote(null);
    const n = Number(threshold);
    if (!(n >= 0)) { setThreshNote({ ok: false, text: "Enter a valid amount" }); return; }
    setThreshold.mutate(n, {
      onSuccess: () => {
        setThreshNote({ ok: true, text: "Saved" });
        qc.invalidateQueries({ queryKey: ["audit-events", "tenants"] });
      },
      onError: (e) => setThreshNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  const saveDirectThreshold = () => {
    setDirectThreshNote(null);
    const n = Number(directThreshold);
    if (!(n >= 0)) { setDirectThreshNote({ ok: false, text: "Enter a valid amount" }); return; }
    setDirectPayThreshold.mutate(n, {
      onSuccess: () => {
        setDirectThreshNote({ ok: true, text: "Saved" });
        qc.invalidateQueries({ queryKey: ["audit-events", "tenants"] });
      },
      onError: (e) => setDirectThreshNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <BuildStamp />
      <JurisdictionSettings me={me} allowed={allowed} />

      <div style={{ font: "600 15px Inter", margin: "28px 0 6px" }}>Payments</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        The UPI VPA that receives payments. Statements and Invoicing generate a standard
        <code style={{ margin: "0 4px" }}>upi://pay</code> link against it — any UPI app opens it directly, no payment
        gateway account needed. A real gateway (Razorpay) is also wired up — see the Pay button next to each invoice.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input value={vpa} onChange={(e) => setVpaLocal(e.target.value)} placeholder="yourbusiness@bank" disabled={!allowed}
          style={{ padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, minWidth: 240, background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        {allowed && (
          <button onClick={save} disabled={setVpa.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {setVpa.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {!allowed && <p style={{ color: "var(--fg-muted)", fontSize: 12, marginTop: 6 }}>Only Admin, CFO, or Finance Controller can change this.</p>}
      {note && <p style={{ fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)", marginTop: 8 }}>{note.text}</p>}

      <div style={{ font: "600 15px Inter", margin: "28px 0 6px" }}>Early-payment discount</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        Offer a discount for paying early — e.g. "2% off if paid within 10 days of the invoice date". Applies
        automatically wherever a customer pays: the Pay button on Statements shows the discounted amount while eligible.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input type="number" min={0} max={100} step={0.5} value={discPct} onChange={(e) => setDiscPct(e.target.value)} disabled={!allowed}
          style={{ width: 70, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>% off if paid within</span>
        <input type="number" min={0} value={discDays} onChange={(e) => setDiscDays(e.target.value)} disabled={!allowed}
          style={{ width: 70, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>days of the invoice date</span>
        {allowed && (
          <button onClick={saveDiscount} disabled={setDiscount.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {setDiscount.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {discNote && <p style={{ fontSize: 13, color: discNote.ok ? "var(--status-good)" : "var(--status-critical)", marginTop: 8 }}>{discNote.text}</p>}

      <div style={{ font: "600 15px Inter", margin: "28px 0 6px" }}>How customers pay, by size</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        Not every customer pays a vendor the same way. A small/individual account can pay by card or UPI on the spot.
        A mid-size account gets invoiced by email and settles it through their own bank on their own schedule. A large
        enterprise account runs it through a formal PO/GRN/vendor-portal process — Invoicing/Statements track PO
        number, GRN and portal status for those instead of showing Pay. Banding is by each customer's total invoiced
        revenue, unless staff set a channel explicitly for that customer.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>Below ({homeCurrency()})</span>
        <input type="number" min={0} step={5000} value={directThreshold} onChange={(e) => setDirectThresholdLocal(e.target.value)} disabled={!allowed}
          style={{ width: 120, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>total invoiced → card/UPI pay (e.g. teleconsults)</span>
        {allowed && (
          <button onClick={saveDirectThreshold} disabled={setDirectPayThreshold.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {setDirectPayThreshold.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {directThreshNote && <p style={{ fontSize: 13, color: directThreshNote.ok ? "var(--status-good)" : "var(--status-critical)", marginTop: -4, marginBottom: 10 }}>{directThreshNote.text}</p>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>At/above ({homeCurrency()})</span>
        <input type="number" min={0} step={10000} value={threshold} onChange={(e) => setThresholdLocal(e.target.value)} disabled={!allowed}
          style={{ width: 140, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)" }} />
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>total invoiced → PO/vendor-portal (large enterprise)</span>
        {allowed && (
          <button onClick={saveThreshold} disabled={setThreshold.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {setThreshold.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      {threshNote && <p style={{ fontSize: 13, color: threshNote.ok ? "var(--status-good)" : "var(--status-critical)", marginTop: 8 }}>{threshNote.text}</p>}
      <p style={{ color: "var(--fg-faint)", fontSize: 12, margin: "8px 0 0" }}>Everything between the two amounts is invoiced by email.</p>

      <div style={{ font: "600 13px Inter", margin: "28px 0 8px" }}>History</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12.5, margin: "0 0 10px" }}>
        Every change to these settings is recorded — who, when, and the exact before/after value.
      </p>
      {audit.isLoading ? <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Loading…</div> : (
        <div style={{ display: "grid", gap: 8 }}>
          {(audit.data ?? []).map((e) => (
            <div key={e.id} style={{ borderLeft: "2px solid var(--border)", paddingLeft: 10, fontSize: 13 }}>
              <div>
                {e.action === "TENANT_EARLY_PAY_DISCOUNT_CHANGED" ? (
                  <>
                    <span style={{ color: "var(--fg-muted)" }}>{e.payload.old_pct}% / {e.payload.old_days}d</span>
                    {" → "}
                    <span style={{ fontWeight: 600 }}>{e.payload.new_pct}% / {e.payload.new_days}d</span>
                  </>
                ) : e.action === "TENANT_PO_PORTAL_THRESHOLD_CHANGED" || e.action === "TENANT_DIRECT_PAY_THRESHOLD_CHANGED" ? (
                  <>
                    <span style={{ color: "var(--fg-muted)" }}>{fmtMoneyFull(e.payload.old_threshold)}</span>
                    {" → "}
                    <span style={{ fontWeight: 600 }}>{fmtMoneyFull(e.payload.new_threshold)}</span>
                  </>
                ) : (
                  <>
                    <span style={{ color: "var(--fg-muted)" }}>{e.payload.old_upi_vpa ?? "(none)"}</span>
                    {" → "}
                    <span style={{ fontWeight: 600 }}>{e.payload.new_upi_vpa ?? "(none)"}</span>
                  </>
                )}
              </div>
              <div style={{ color: "var(--fg-faint)", fontSize: 11 }}>
                {e.actor_label ?? "unknown"} · {fmtDateTime(e.created_at)}
              </div>
            </div>
          ))}
          {(audit.data ?? []).length === 0 && <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>No changes recorded yet.</div>}
        </div>
      )}

      <div id="erp-sync"><ErpSync canWrite={canWrite} /></div>
      <div id="digests"><DigestSettings canWrite={canWrite} /></div>
      <div id="enterprise"><EnterpriseSettings canWrite={canWrite} /></div>
    </div>
  );
}
